import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import { makeStyles, MenuItem, IconButton, Link, List, Alert, CloudDownload as CloudDownloadIcon, ArrowDropDown as ArrowDropDownIcon, ArrowDropUp as ArrowDropUpIcon, Settings as SettingsIcon, Close as CloseIcon, Help as HelpIcon, Warning as WarningIcon, MenuBook as MenuBookIcon, } from '@vitessce/styles';
import { TOOLTIP_ANCESTOR } from './classNames.js';
import LoadingIndicator from './LoadingIndicator.js';
import { PopperMenu } from './shared-mui/components.js';
import { useTitleStyles } from './title-styles.js';
const useStyles = makeStyles()(theme => ({
    iconButton: {
        border: 'none',
        marginLeft: 0,
        background: 'none',
        color: theme.palette.primaryForeground,
        paddingLeft: '0.25em',
        paddingRight: '0.25em',
        borderRadius: '2px',
        '&:hover': {
            backgroundColor: theme.palette.primaryBackgroundLight,
        },
        '&:first-of-type': {
            marginLeft: '0.25em',
        },
        '&:last-child': {
            marginRight: '0.25em',
        },
        '& svg': {
            width: '0.7em',
            height: '0.7em',
            verticalAlign: 'middle',
            overflow: 'visible',
        },
    },
    downloadLink: {
        color: theme.palette.primaryForeground,
    },
    helpTextSpan: {
        maxWidth: '400px',
        padding: '5px 10px',
        display: 'inline-block',
        textAlign: 'justify',
        fontSize: '14px',
        backgroundColor: theme.palette.gridLayoutBackground,
        color: theme.palette.tooltipText,
        borderRadius: '8px',
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
        border: '10px solid grey',
    },
    errorList: {
        width: '100%',
        maxWidth: 300,
        padding: '0 4px',
    },
    errorListAlert: {
        marginTop: '2px',
        marginBottom: '2px',
    },
    errorListItemText: {
        fontSize: '12px',
    },
}));
function SettingsIconWithArrow({ open }) {
    return (_jsxs(_Fragment, { children: [_jsx(SettingsIcon, {}), open ? _jsx(ArrowDropUpIcon, {}) : _jsx(ArrowDropDownIcon, {})] }));
}
function PlotOptions(props) {
    const { options } = props;
    const [open, setOpen] = useState(false);
    const { classes } = useStyles();
    const buttonIcon = useMemo(() => (_jsx(SettingsIconWithArrow, { open: open })), [open]);
    return (options ? (_jsx(PopperMenu, { open: open, setOpen: setOpen, buttonIcon: buttonIcon, buttonClassName: classes.iconButton, placement: "bottom-end", title: "Plot Options", "aria-label": "Open plot options menu", children: options })) : null);
}
function CloudDownloadIconWithArrow({ open }) {
    return (_jsxs(_Fragment, { children: [_jsx(CloudDownloadIcon, {}), open ? _jsx(ArrowDropUpIcon, {}) : _jsx(ArrowDropDownIcon, {})] }));
}
function DownloadOptions(props) {
    const { urls } = props;
    const [open, setOpen] = useState(false);
    const { classes } = useStyles();
    const buttonIcon = useMemo(() => (_jsx(CloudDownloadIconWithArrow, { open: open })), [open]);
    return (urls && urls.length ? (_jsx(PopperMenu, { open: open, setOpen: setOpen, buttonIcon: buttonIcon, buttonClassName: classes.iconButton, placement: "bottom-end", title: "Download Options", "aria-label": "Open download options menu", children: urls.map(({ url, name }) => (_jsx(MenuItem, { dense: true, "aria-label": `Click to download ${name}`, children: _jsxs(Link, { underline: "always", href: url, target: "_blank", rel: "noopener", className: classes.downloadLink, children: ["Download ", name] }) }, `${url}_${name}`))) })) : null);
}
function HelpButton(props) {
    const { helpText, guideUrl } = props;
    const [open, setOpen] = useState(false);
    const { classes } = useStyles();
    return (_jsx(PopperMenu, { open: open, setOpen: setOpen, buttonIcon: _jsx(HelpIcon, {}), buttonClassName: classes.iconButton, placement: "bottom-end", title: "Help Info", "aria-label": "Open help info", withPaper: false, children: _jsxs("span", { className: classes.helpTextSpan, children: [helpText, guideUrl ? (_jsx(IconButton, { component: "a", href: guideUrl, target: "_blank", rel: "noopener noreferrer", size: "small", className: classes.iconButton, title: "Open Navigation Guide", "aria-label": "Open navigation guide", children: _jsx(MenuBookIcon, {}) })) : null] }) }));
}
function ErrorInfo(props) {
    const { errors } = props;
    const [open, setOpen] = useState(false);
    const { classes } = useStyles();
    return (_jsx(PopperMenu, { open: open, setOpen: setOpen, buttonIcon: _jsx(WarningIcon, { color: "error" }), buttonClassName: classes.iconButton, placement: "bottom-end", title: "View Errors", "aria-label": "Open error info", children: _jsx(List, { className: classes.errorList, children: errors.map((error, index) => (_jsxs(Alert
            // eslint-disable-next-line react/no-array-index-key
            , { severity: "error", className: classes.errorListAlert, slots: { message: 'span' }, slotProps: { message: { className: classes.errorListItemText } }, children: [error.name, ": ", error.message] }, `${index}-${error.name}-${error.message}`))) }) }));
}
function ClosePaneButton(props) {
    const { removeGridComponent } = props;
    const { classes } = useStyles();
    return (_jsx(IconButton, { onClick: removeGridComponent, size: "small", className: classes.iconButton, title: "Close View", "aria-label": "Close panel button", children: _jsx(CloseIcon, {}) }));
}
export function TitleInfo(props) {
    const { title, info, children, isScroll, isSpatial, removeGridComponent, urls, isReady, options, closeButtonVisible = true, downloadButtonVisible = true, helpText, withPadding = true, errors: errorsProp, guideUrl, } = props;
    const errors = errorsProp?.filter(Boolean);
    const { classes } = useTitleStyles();
    const hasInfoText = Boolean(info);
    return (
    // d-flex without wrapping div is not always full height; I don't understand the root cause.
    _jsxs(_Fragment, { children: [_jsxs("div", { className: classes.title, role: "banner", children: [_jsx("div", { className: classes.titleLeft, role: "heading", "aria-level": "1", children: title }), _jsxs("div", { className: classes.titleButtons, role: "toolbar", "aria-label": "Plot options and controls", children: [_jsx(PlotOptions, { options: options }), downloadButtonVisible ? (_jsx(DownloadOptions, { urls: urls })) : null, Array.isArray(errors) && errors.length > 0 ? (_jsx(ErrorInfo, { errors: errors })) : null, helpText ? (_jsx(HelpButton, { helpText: helpText, guideUrl: guideUrl })) : null, closeButtonVisible && removeGridComponent ? (_jsx(ClosePaneButton, { removeGridComponent: removeGridComponent })) : null] })] }), _jsxs("div", { className: clsx(TOOLTIP_ANCESTOR, classes.card, {
                    [classes.scrollCard]: isScroll,
                    [classes.spatialCard]: isSpatial,
                    [classes.noScrollCard]: !isScroll && !isSpatial,
                    [classes.noPaddingCard]: !withPadding,
                    [classes.paddingCard]: withPadding,
                    [classes.cardWithoutInfoText]: !hasInfoText,
                }), "aria-busy": !isReady, role: "main", children: [!isReady ? _jsx(LoadingIndicator, {}) : null, children] }), hasInfoText ? (_jsx("div", { className: classes.infoText, title: info, role: "note", children: info })) : null] })
    // "pl-2" only matters when the window is very narrow.
    );
}
//# sourceMappingURL=TitleInfo.js.map